import { useContext, useRef } from "react";

import { useTranslation } from "react-i18next";
import { CSSTransition } from "react-transition-group";

import { css } from "@emotion/react";

import { colors } from "@dotnet/telematics-design-system";

import ListChoice from "components/DesignComponents/ListChoice";
import Separator from "components/DesignComponents/Separator";
import { renderTranslatedDescription } from "context/notifications/functions";
import NotificationsContext from "context/notifications/notificationsContext";

import ActionBox from "./ActionBox";
import StatusBadge from "./StatusBadge";
import { SingleNotificationProps } from "./types";

const SingleNotification = ({
    data,
    in: inProp,
    onExited,
    onEnter,
    onEntering,
    onEntered,
    onExit,
    onExiting
}: SingleNotificationProps) => {
    const { gray200 } = colors;

    const { pushPreviousNotification } = useContext(NotificationsContext);

    const { t } = useTranslation();

    const nodeRef = useRef(null);

    const {
        created_at,
        data: { messages },
        id,
        read_at,
        type
    } = data;

    const getStatus = () => {
        const separateWords = type.replace(/([a-z])([A-Z])/g, "$1 $2");
        const splitWords = separateWords.split(" ");

        return splitWords[splitWords.length - 1];
    };

    const status = getStatus();
    const typeSplit = type.split("\\");
    const typeName = typeSplit[typeSplit.length - 1];

    return (
        <CSSTransition
            in={inProp}
            onEnter={onEnter}
            onEntering={onEntering}
            onEntered={onEntered}
            onExit={onExit}
            onExiting={onExiting}
            nodeRef={nodeRef}
            timeout={300}
            classNames="slide"
            onExited={(node: HTMLElement) => {
                onExited?.(node);
                pushPreviousNotification();
            }}
        >
            <>
                <ListChoice
                    ref={nodeRef}
                    title={t(`Notifications##title##${typeName}`)}
                    description={
                        <>
                            {messages.map(message => (
                                <span key={message}>
                                    {renderTranslatedDescription(message, t)}
                                </span>
                            ))}
                        </>
                    }
                    iconLeft={<StatusBadge status={status} />}
                    iconRight={<ActionBox id={id} date={created_at} />}
                    selected={read_at === null}
                    isNotClickable
                    isNotification
                    isNormalWhiteSpace
                    css={css({
                        userSelect: "auto"
                    })}
                />

                <Separator
                    isListSeparator
                    css={css({
                        background: gray200
                    })}
                />
            </>
        </CSSTransition>
    );
};

export default SingleNotification;
